import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import StudentForm from '../../components/forms/StudentForm';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers, supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

const StudentEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (company?.id && id) {
      loadStudent();
    }
  }, [company, id]);

  const loadStudent = async () => {
    try {
      setLoading(true);
      console.log('Loading student for edit with ID:', id, 'for company:', company.id);
      
      const foundStudent = await dataHelpers.getStudentById(company.id, id);
      
      if (!foundStudent) {
        console.log('Student not found with ID:', id);
        toast.error('Aluno não encontrado');
        navigate('/dashboard/students');
        return;
      }
        // Adaptar dados para o formato esperado pelo formulário
      const studentData = {
        ...foundStudent,
        // Manter dados do próprio aluno
        name: foundStudent.name || '',
        email: foundStudent.email || '',
        phone: foundStudent.phone || '',
        // IDs dos responsáveis para seleção no formulário
        selected_parent_id: foundStudent.person_id || '',
        selected_financial_parent_id: foundStudent.financial_parent_id || '',
      };
      
      // Remove password fields to prevent database errors during edit
      delete studentData.password;
      delete studentData.confirm_password;
      
      // Remove campos relacionais que não devem ir para o update
      delete studentData.person;
      delete studentData.financial_person;
      delete studentData.people;
      
      console.log('Student loaded for edit:', studentData);
      setStudent(studentData);
    } catch (error) {
      console.error('Error loading student:', error);
      toast.error('Erro ao carregar aluno');
      navigate('/dashboard/students');
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = async (studentData) => {
    try {
      setSaving(true);
      
      console.log('Updating student with data:', studentData);
      
      // Verificar se houve mudança no saldo
      const oldBalance = parseFloat(student.balance || 0);
      const newBalance = parseFloat(studentData.balance || 0);
      const balanceDifference = newBalance - oldBalance;
      
      console.log('🔍 Debug saldo na edição:', {
        oldBalance,
        newBalance,
        difference: balanceDifference,
        shouldCreateTransaction: balanceDifference !== 0
      });
      
      await dataHelpers.updateStudent(student.id, studentData);
      
      // Se houve mudança no saldo, criar transação de ajuste
      if (balanceDifference !== 0) {
        console.log('💰 Criando transação de ajuste de saldo:', balanceDifference);
        
        try {
          const transactionData = {
            id: crypto.randomUUID(),
            company_id: company.id,
            student_id: student.id,
            type: 'credit',
            transaction_type: balanceDifference > 0 ? 'credit' : 'debit',
            description: balanceDifference > 0 
              ? `Ajuste de saldo (acréscimo) - ${student.name}`
              : `Ajuste de saldo (redução) - ${student.name}`,
            amount: Math.abs(balanceDifference),
            balance_before: oldBalance,
            balance_after: newBalance,
            payment_method: 'balance_adjustment',
            details: {
              balance_adjustment: true,
              student_name: student.name,
              old_balance: oldBalance,
              new_balance: newBalance,
              created_via: 'STUDENT_EDIT'
            },
            status: 'completed',
            created_by: student.financial_parent_id || '00000000-0000-0000-0000-000000000000',
            created_at: new Date().toISOString()
          };
          
          console.log('📝 Dados da transação de ajuste:', transactionData);
          
          const { data: insertResult, error: transactionError } = await supabase
            .from('financial_transactions')
            .insert(transactionData)
            .select()
            .single();
          
          console.log('📊 Resultado da inserção de ajuste:', { insertResult, transactionError });
          
          if (transactionError) {
            console.error('❌ Erro ao criar transação de ajuste:', transactionError);
            toast.error('Aluno atualizado, mas houve erro ao registrar ajuste de saldo');
          } else {
            console.log('✅ Transação de ajuste criada com sucesso:', insertResult);
            toast.success('Aluno atualizado com sucesso e ajuste de saldo registrado!');
          }
        } catch (transactionError) {
          console.error('Erro ao criar transação de ajuste:', transactionError);
          toast.error('Aluno atualizado, mas houve erro ao registrar ajuste de saldo');
        }
      } else {
        toast.success('Aluno atualizado com sucesso!');
      }
      
      // Redirecionar para a página de detalhes
      navigate(`/dashboard/students/${student.id}`, {
        state: { message: 'Aluno atualizado com sucesso!' }
      });
      
    } catch (error) {
      console.error('Error updating student:', error);
      
      let errorMessage = 'Erro ao atualizar aluno';
      
      if (error.message?.includes('duplicate')) {
        errorMessage = 'Já existe um aluno com esta matrícula';
      } else if (error.message?.includes('registration_number')) {
        errorMessage = 'Número de matrícula já está em uso';
      } else if (error.message?.includes('email')) {
        errorMessage = 'Este email já está cadastrado no sistema';
      } else if (error.message?.includes('company_id')) {
        errorMessage = 'Erro de autorização. Verifique se você tem permissão para editar alunos';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/dashboard/students/${student.id}`);
  };

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  if (!student) {
    return (
      
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Aluno não encontrado</h2>
          <p className="text-gray-600 mb-6">O aluno que você está procurando não existe ou foi removido.</p>          <Button 
            onClick={() => navigate('/dashboard/students')}
            className="h-10 px-4"
          >
            Voltar para Alunos
          </Button>
        </div>
      
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">            <Button
              variant="outline"
              onClick={() => navigate(`/dashboard/students/${student.id}`)}
              className="flex items-center h-10 px-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Editar Aluno</h1>
              <p className="text-gray-600">
                Editando: {student.name} • Matrícula: {student.registration_number}
              </p>
            </div>
          </div>
        </div>

        {/* Informações da empresa */}
        {company && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <Users className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-blue-800">
                Editando aluno para: <strong>{company.name}</strong>
              </span>
            </div>
          </div>
        )}

        {/* Aviso sobre edição */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-yellow-700">
              <p className="font-medium">Atenção ao editar dados do aluno</p>
              <p className="mt-1">
                Alterações nos dados pessoais podem afetar o acesso ao sistema. 
                A senha não será alterada durante a edição.
              </p>
            </div>
          </div>
        </div>

        {/* Formulário */}
        <StudentForm
          initialData={student}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={saving}
        />
      </div>
    </>
  );
};

export default StudentEditPage;
